import grey from "@/design/colors/grey";
import ruby from "@/design/colors/ruby";
import trueBlue from "@/design/colors/trueBlue";

import { green, orange, red } from "@mui/material/colors";

export const designTokens = {
  cssVariables: {
    colorSchemeSelector: "data",
  },
  colorSchemes: {
    light: {
      palette: {
        body: "#000",
        background: {
          default: "#f8f8f8",
          card: "#fff",
          pagination: "#fff",
        },
        divider: "#e0e0e0",
        dividerHover: "rgba(0 0 0 / .87)",
        primary: trueBlue,
        secondary: ruby,
        accept: {
          main: green[50],
          border: green[600],
          text: green[900],
        },
        mixed: {
          main: orange[50],
          border: orange[600],
          text: orange[900],
        },
        deny: {
          main: red[50],
          border: red[600],
          text: red[900],
        },
      },
    },
    dark: {
      palette: {
        body: "#fff",
        background: {
          default: grey[900],
          paper: grey[900],
          card: "#1f1f24",
          pagination: "#2e2e32",
        },
        divider: "rgba(255 255 255 / .075)",
        dividerHover: "#fff",
        primary: trueBlue,
        secondary: ruby,
        grey,
        accept: {
          main: `rgba(67,160,71,.15)`,
          border: green[900],
          text: "#fff",
        },
        mixed: {
          main: `rgba(251,140,0,.15)`,
          border: orange[900],
          text: "#fff",
        },
        deny: {
          main: `rgba(229,57,53,.15)`,
          border: red[900],
          text: "#fff",
        },
      },
    },
  },
  shape: {
    borderRadius: 8,
  },
  acrylic: {
    backgroundColor: (theme) => `color-mix(in srgb, ${theme.vars.palette.background.card} 85%, transparent)`,
    backdropFilter: "blur(15px) saturate(125%)",
  },
  scrollbar: {
    scrollbarWidth: "thin",
    scrollbarColor: (theme) => `${theme.vars.palette.grey[500]} transparent`,
  },
  shadows: [
    "none",
    ".1px .2px .4px rgba(0, 0, 0, .028), .2px .7px 1.3px rgba(0, 0, 0, .042), 1px 3px 6px rgba(0, 0, 0, .07)",
    ".1px .2px .4px rgba(0, 0, 0, .04), .2px .7px 1.3px rgba(0, 0, 0, .06), 1px 3px 6px rgba(0, 0, 0, .1)",
    ".1px .3px .4px rgba(0, 0, 0, .044), .2px .9px 1.3px rgba(0, 0, 0, .066), 1px 4px 6px rgba(0, 0, 0, .11)",
    ".1px .3px .55px rgba(0, 0, 0, .053), .2px .9px 2px rgba(0, 0, 0, .077), 1px 4px 10px rgba(0, 0, 0, .13)",
    "1px 1px 9px rgba(0,0,0,.19)",
    ".3px .5px .8px rgba(0, 0, 0, .057), .9px 1.6px 2.7px rgba(0, 0, 0, .083), 4px 7px 12px rgba(0, 0, 0, .14)",
    "1px 1px 10px rgba(0,0,0,.22)",
    ".1px .3px .55px rgba(0, 0, 0, .053), .2px .9px 2px rgba(0, 0, 0, .077), 1px 4px 10px rgba(0, 0, 0, .13)",
    "2px 2px 11px rgba(0,0,0,.26)",
    "2px 2px 12px rgba(0,0,0,.27)",
    "2px 2px 12px rgba(0,0,0,.28)",
    "2px 2px 13px rgba(0,0,0,.29)",
    "2px 2px 13px rgba(0,0,0,.30)",
    "2px 2px 14px rgba(0,0,0,.30)",
    "2px 2px 15px rgba(0,0,0,.32)",
    "2px 2px 16px rgba(0,0,0,.32)",
    "3px 3px 17px rgba(0,0,0,.34)",
    "3px 3px 18px rgba(0,0,0,.34)",
    "3px 3px 19px rgba(0,0,0,.36)",
    "3px 3px 20px rgba(0,0,0,.36)",
    "3px 3px 21px rgba(0,0,0,.38)",
    "3px 3px 22px rgba(0,0,0,.38)",
    "0 8px 16px -10px rgba(0,0,0,.3)",
    ".3px .5px 2.7px rgba(0, 0, 0, .081), .9px 1.6px 8.9px rgba(0, 0, 0, .119), 4px 7px 40px rgba(0, 0, 0, .2)",
  ],
  typography: {
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI Variable Text', 'Segoe UI', Roboto, Helvetica Neue, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'",
    h1: {
      fontWeight: 600,
      fontSize: "2.3rem",
      marginBottom: (theme) => theme.spacing(1),
      fontFamily:
        "-apple-system, BlinkMacSystemFont, 'Segoe UI Variable Display', 'Segoe UI', Roboto, Helvetica Neue, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'",
    },
    h2: {
      fontWeight: 600,
      fontSize: "2rem",
      marginBottom: (theme) => theme.spacing(1),
      fontFamily:
        "-apple-system, BlinkMacSystemFont, 'Segoe UI Variable Display', 'Segoe UI', Roboto, Helvetica Neue, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'",
    },
    h3: {
      fontWeight: 600,
      fontSize: "1.8rem",
      marginTop: (theme) => theme.spacing(1),
      marginBottom: (theme) => theme.spacing(1),
      fontFamily:
        "-apple-system, BlinkMacSystemFont, 'Segoe UI Variable Display', 'Segoe UI', Roboto, Helvetica Neue, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'",
    },
    h4: {
      fontWeight: 600,
      fontSize: "1.6rem",
      fontFamily:
        "-apple-system, BlinkMacSystemFont, 'Segoe UI Variable Display', 'Segoe UI', Roboto, Helvetica Neue, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'",
    },
    h5: {
      fontWeight: 600,
      fontSize: "1.15rem",
      marginTop: (theme) => theme.spacing(1),
      marginBottom: (theme) => theme.spacing(1),
      fontFamily:
        "-apple-system, BlinkMacSystemFont, 'Segoe UI Variable Display', 'Segoe UI', Roboto, Helvetica Neue, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'",
    },
    h6: {
      fontWeight: 600,
      fontSize: "1rem",
      marginTop: (theme) => theme.spacing(1),
      marginBottom: (theme) => theme.spacing(1),
      fontFamily:
        "-apple-system, BlinkMacSystemFont, 'Segoe UI Variable Display', 'Segoe UI', Roboto, Helvetica Neue, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'",
    },
    overline: {
      lineHeight: 1,
      fontWeight: 600,
      fontSize: ".75rem",
      textTransform: "initial",
    },
  },
  breakpoints: {
    keys: ["xs", "sm", "md", "lg", "xl", "xxl"],
    values: {
      xs: 0,
      sm: 576,
      md: 768,
      lg: 1050,
      xl: 1500,
      xxl: 1920,
    },
  },
  components: {
    MuiAlert: {
      styleOverrides: {
        root: {
          borderWidth: 1,
          borderStyle: "solid",
          variants: [
            {
              props: { variant: "standard", severity: "success" },
              style: ({ theme }) => ({
                borderColor: theme.vars.palette.success.light,
                ...theme.applyStyles("dark", {
                  borderColor: theme.vars.palette.success.dark,
                }),
              }),
            },
            {
              props: { variant: "standard", severity: "info" },
              style: ({ theme }) => ({
                borderColor: theme.vars.palette.info.light,
                ...theme.applyStyles("dark", {
                  borderColor: theme.vars.palette.info.dark,
                }),
              }),
            },
            {
              props: { variant: "standard", severity: "warning" },
              style: ({ theme }) => ({
                borderColor: theme.vars.palette.warning.light,
                ...theme.applyStyles("dark", {
                  borderColor: theme.vars.palette.warning.dark,
                }),
              }),
            },
            {
              props: { variant: "standard", severity: "error" },
              style: ({ theme }) => ({
                borderColor: theme.vars.palette.error.light,
                ...theme.applyStyles("dark", {
                  borderColor: theme.vars.palette.error.dark,
                }),
              }),
            },
          ],
        },
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        slotProps: {
          paper: {
            elevation: 3,
          },
        },
      },
      styleOverrides: {
        listbox: ({ theme }) => ({
          contain: "paint",
          padding: 4,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          colorScheme: "light",
          "&:has(.MuiAutocomplete-groupUl)": {
            padding: 0,
          },
          ...theme.applyStyles("dark", {
            colorScheme: "dark",
          }),
          ...theme.scrollbar,
        }),
        option: {
          marginInline: 0,
          paddingInline: "8px !important",
          borderRadius: 6,
          flexShrink: 0,
        },
        groupUl: {
          display: "flex",
          flexDirection: "column",
          gap: 2,
          margin: 4,
        },
        groupLabel: ({ theme }) => ({
          marginInline: 0,
          borderRadius: 0,
          lineHeight: "36px",
          top: 0,
          borderBottom: `1px solid ${theme.vars.palette.divider}`,
          backgroundColor: theme.vars.palette.background.card,
        }),
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          textTransform: "initial !important",
          minWidth: "auto !important",
        },
        icon: {
          marginBottom: 3,
        },
      },
    },
    MuiDrawer: {
      defaultProps: {
        slotProps: {
          paper: {
            elevation: 0,
          },
        },
      },
      styleOverrides: {
        root: ({ theme }) => ({
          colorScheme: "light",
          ...theme.applyStyles("dark", {
            colorScheme: "#323237",
          }),
        }),
      },
    },
    MuiPopover: {
      defaultProps: {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "left",
        },
        transformOrigin: {
          vertical: "top",
          horizontal: "left",
        },
        slotProps: {
          paper: {
            elevation: 2,
          },
        },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        positionEnd: {
          marginLeft: 4,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          minHeight: 36,
          minWidth: 36,
          padding: theme.spacing(0.75, 1.5),
          textTransform: "initial",
          whiteSpace: "nowrap !important",
          variants: [
            { props: { size: "small" }, style: { "& .MuiButton-icon": { fontSize: ".8125rem" } } },
            { props: { size: "medium" }, style: { "& .MuiButton-icon": { fontSize: "1rem" } } },
            { props: { size: "large" }, style: { "& .MuiButton-icon": { fontSize: "1rem" } } },
          ],
        }),
        sizeSmall: ({ theme }) => ({
          minHeight: 20,
          minWidth: 20,
          padding: theme.spacing(0.5, 1.25),
        }),
        sizeMedium: {
          height: 36,
        },
        startIcon: {
          marginLeft: 0,
          "& > *:nth-of-type(1)": {
            fontSize: "inherit",
          },
        },
        endIcon: {
          marginRight: 0,
          "& > *:nth-of-type(1)": {
            fontSize: "inherit",
          },
        },
      },
    },
    MuiButtonGroup: {
      styleOverrides: {
        root: {
          "& > .MuiButton-root + .MuiButton-root": {
            marginLeft: 0,
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          "&:last-child": {
            paddingBottom: theme.spacing(2),
          },
        }),
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.spacing(1.5),
          height: theme.spacing(7.5),
        }),
        action: {
          marginTop: 0,
          marginRight: 0,
          paddingRight: 0,
          display: "flex",
          alignItems: "center",
          "& > div": {
            display: "block",
          },
        },
        avatar: ({ theme }) => ({
          marginRight: theme.spacing(1.5),
        }),
        title: {
          fontWeight: 600,
          marginBottom: -4,
          fontSize: "1.125rem",
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI Variable Display', 'Segoe UI', Roboto, Helvetica Neue, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'",
        },
        subheader: {
          maginTop: -2,
        },
        content: ({ theme }) => ({
          minWidth: 0,
          marginRight: theme.spacing(2),
          "& > *": {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ theme }) => ({
          margin: theme.spacing(0.25),
        }),
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderColor: theme.vars.palette.divider,
          borderRightWidth: 1,
        }),
      },
    },
    MuiDialog: {
      defaultProps: {
        slotProps: {
          paper: {
            elevation: 1,
          },
        },
      },
      styleOverrides: {
        paper: ({ theme }) => ({
          borderRadius: theme.spacing(2),
          padding: theme.spacing(2),
          width: "100%",
          background: theme.vars.palette.background.default,
          boxShadow: theme.shadows[24],
          overflow: "auto",
        }),
        backdrop: ({ theme }) => ({
          backdropFilter: "blur(5px)",
          backgroundColor: "rgba(0,0,0,.25)",
          ...theme.applyStyles("dark", {
            backgroundColor: "rgba(255,255,255,.075)",
          }),
        }),
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.spacing(1.5, 3),
          "&.MuiPickersLayout-actionBar": {
            padding: theme.spacing(1),
          },
        }),
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          margin: 0,
          fontSize: "1.25rem",
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 8,
          padding: theme.spacing(1.25),
          fontSize: 16,
          variants: [
            {
              props: { variant: "avatar" },
              style: ({ theme }) => ({
                padding: 4,
                borderRadius: 80,
                "& .MuiAvatar-root": {
                  marginLeft: theme.spacing(0.5),
                },
              }),
            },
          ],
        }),
        sizeSmall: ({ theme }) => ({
          padding: theme.spacing(0.75),
        }),
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          variants: [
            {
              props: { variant: "indeterminate", size: "large" },
              style: {
                borderRadius: 8,
                height: 6,
                "& .MuiLinearProgress-bar": {
                  borderRadius: 8,
                },
              },
            },
          ],
        },
        bar: {
          borderRadius: 8,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 6,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        list: {
          padding: 4,
        },
      },
    },
    MuiList: {
      defaultProps: {
        disablePadding: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(0.25),
        }),
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          minHeight: theme.spacing(7),
        }),
      },
    },
    MuiListItemAvatar: {
      styleOverrides: {
        root: ({ theme }) => ({
          minWidth: theme.spacing(5.5),
        }),
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          minHeight: theme.spacing(7),
          borderRadius: theme.spacing(1),
          overflow: "hidden",
          "&.Mui-selected": {
            "& .MuiListItemText-root": {
              color: theme.vars.palette.primary[500],
              ...theme.applyStyles("dark", {
                color: theme.vars.palette.primary[300],
              }),
            },
            "& .MuiListItemIcon-root svg": {
              color: theme.vars.palette.primary[500],
              ...theme.applyStyles("dark", {
                color: theme.vars.palette.primary[300],
              }),
            },
            "&::after": {
              transition: "all .2s ease-in-out",
              "@media (prefers-reduced-motion: reduce)": {
                transition: "all 0s ease-in-out",
              },
              position: "absolute",
              backgroundColor: theme.vars.palette.primary[500],
              height: 16,
              width: 4,
              borderRadius: 2,
              top: "calc(50% - 8px)",
              left: 0,
              content: `""`,
            },
          },
        }),
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.spacing(0.75, 0.5),
          margin: 0,
          fontSize: 16,
          minWidth: 34,
          color: "#333",
          ...theme.applyStyles("dark", {
            color: "#fff",
          }),
        }),
      },
    },
    MuiListItemSecondaryAction: {
      styleOverrides: {
        root: {
          right: 12,
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          margin: 0,
        },
        primary: {
          minWidth: 0,
          display: "block",
          fontSize: ".94rem",
          lineHeight: "18px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        },
        secondary: {
          minWidth: 0,
          display: "block",
          fontSize: ".75rem",
          lineHeight: "14px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        fullWidth: true,
        type: "text",
        size: "small",
      },
    },
    MuiPaper: {
      variants: [
        {
          props: { variant: "acrylic" },
          style: ({ theme }) => ({
            boxShadow: theme.shadows[4],
            backgroundColor: `color-mix(in srgb, ${theme.vars.palette.background.pagination} 90%, transparent)`,
            backdropFilter: "blur(15px) saturate(125%)",
          }),
        },
      ],
      styleOverrides: {
        outlined: ({ theme }) => ({
          backgroundColor: "transparent",
          borderColor: theme.vars.palette.divider,
        }),
      },
    },
    MuiPagination: {
      defaultProps: {
        color: "primary",
        siblingCount: 1,
        boundaryCount: 0,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          wrap: "nowrap",
          boxShadow: theme.shadows[4],
          display: "inline-block",
          contain: "paint",
          borderRadius: 8,
          backgroundColor: `color-mix(in srgb, ${theme.vars.palette.background.pagination} 90%, transparent)`,
          backdropFilter: "blur(15px) saturate(125%)",
        }),
        ul: {
          "& > li:first-of-type button": {
            marginLeft: 0,
          },
          "& > li:last-of-type button": {
            marginRight: 0,
          },
        },
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          margin: 0,
          borderRadius: 0,
          minWidth: "36px !important",
          height: 36,
          fontSize: "1rem",
        },
        ellipsis: {
          display: "none",
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        selectMenu: ({ theme }) => ({
          lineHeight: "24px",
          height: theme.spacing(3),
        }),
        select: {
          borderRadius: 8,
          "&:focus": {
            borderRadius: 8,
          },
          "& > .MuiButton-root": {
            display: "none",
          },
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          "@media (pointer: coarse)": {
            padding: 0,
          },
        },
        track: {
          border: 0,
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: ({ theme }) => ({
          width: 30,
          height: 18,
          padding: 0,
          margin: theme.spacing(0.25, 0),
        }),
        switchBase: ({ theme }) => ({
          padding: 3,
          "&.Mui-checked": {
            transform: "translateX(12px)",
            color: theme.vars.palette.common.white,
            "& + .MuiSwitch-track": {
              opacity: 1,
            },
            "&.Mui-disabled + .MuiSwitch-track": {
              opacity: 0.5,
            },
          },
        }),
        thumb: {
          width: 12,
          height: 12,
          top: -2,
          color: "#fff",
          boxShadow: "none",
        },
        track: ({ theme }) => ({
          borderRadius: 26 / 2,
          border: `none`,
          backgroundColor: theme.vars.palette.grey[400],
          transition: theme.transitions.create(["background-color"]),
          "@media (prefers-reduced-motion: reduce)": {
            transition: "all 0s ease-in-out",
          },
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          transition: "all .2s ease-in-out",
          "@media (prefers-reduced-motion: reduce)": {
            transition: "all 0s ease-in-out",
          },
          position: "relative",
          flexDirection: "row",
          minHeight: 36,
          padding: theme.spacing(1, 1.5),
          fontSize: 16,
          minWidth: "0 !important",
          textTransform: "capitalize",
          borderRadius: 8,
          gap: 8,
          ".MuiTab-icon": {
            marginBottom: "-2px",
          },
          "&:hover": {
            backgroundColor: `color-mix(in srgb, ${theme.vars.palette.primary[500]} 15%, transparent)`,
          },
          "&.Mui-selected": {
            "&::after": {
              transition: "all .2s ease-in-out",
              "@media (prefers-reduced-motion: reduce)": {
                transition: "all 0s ease-in-out",
              },
              position: "absolute",
              background: theme.vars.palette.primary[500],
              width: 16,
              height: 4,
              borderRadius: 3,
              bottom: 0,
              left: "calc(50% - 8px)",
              content: `""`,
            },
          },
        }),
        fullWidth: {
          "&.Mui-selected": {
            "& ::after": {
              background: "#fff",
            },
          },
        },
        labelIcon: ({ theme }) => ({
          padding: `${theme.spacing(0.5, 1.5)} !important`,
          "& > span > svg": {
            width: 16,
            height: 16,
          },
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          "&:not(:last-child)": {
            paddingRight: 0,
          },
          borderBottom: 0,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderBottom: "1px solid",
          borderBottomColor: "rgba(0 0 0 / .05)",
          ...theme.applyStyles("dark", {
            borderBottomColor: "rgba(255 255 255 / .075)",
          }),
          "&:last-child:not(.MuiTableRow-head)": {
            borderBottom: "none !important",
          },
          "&.Mui-selected": {
            backgroundColor: `color-mix(in srgb, ${theme.vars.palette.primary[500]} 10%, transparent)`,
            "&:hover": {
              backgroundColor: `color-mix(in srgb, ${theme.vars.palette.primary[500]} 20%, transparent)`,
            },
          },
        }),
      },
    },
    MuiTabs: {
      defaultProps: {
        indicatorColor: "primary",
        textColor: "primary",
      },
      styleOverrides: {
        root: ({ theme }) => ({
          position: "relative",
          minHeight: 36,
          margin: theme.spacing(0),
          alignItems: "flex-end",
        }),
        indicator: {
          display: "none",
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: "#000",
          ...theme.applyStyles("dark", {
            color: "#fff",
          }),
          minHeight: `${theme.spacing(6)} !important`,
          alignItems: "center",
        }),
        gutters: ({ theme }) => ({
          padding: `${theme.spacing(0, 1)} !important`,
        }),
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: ({ theme }) => ({
          backgroundColor: "#fff",
          boxShadow: theme.shadows[5],
          color: "#000",
          fontSize: 12,
          padding: theme.spacing(0.75, 1),
          "& p": {
            fontSize: 12,
          },
        }),
      },
    },
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          color: "#000",
          variants: [
            {
              props: { variant: "transparent" },
              style: ({ theme }) => ({
                backgroundColor: "transparent",
                borderTop: `1px solid ${theme.vars.palette.divider}`,
                borderBottom: `1px solid ${theme.vars.palette.divider}`,
              }),
            },
          ],
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        colorDefault: ({ theme }) => ({
          color: theme.vars.palette.primary[500],
          backgroundColor: theme.vars.palette.primary[100],
          ...theme.applyStyles("dark", {
            color: theme.vars.palette.primary[100],
            backgroundColor: theme.vars.palette.primary[500],
          }),
        }),
      },
    },
    MuiMultiSectionDigitalClockSection: {
      styleOverrides: {
        root: {
          padding: 4,
          gap: 2,
          "&::after": {
            content: "initial",
          },
        },
        item: {
          borderRadius: 4,
          margin: "0 !important",
          padding: 4,
        },
      },
    },
  },
};
